// CreateCourse.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiTrash2,
  FiMove,
  FiPlus,
} from "react-icons/fi";
import { FaWhatsapp, FaMicrosoft } from "react-icons/fa";
import SharedLayout from "./SharedLayout";
import "./App.css";
import apiClient from "./services/apiClient";
import ChannelSelector from "./components/ChannelSelector";
import DatePicker from "react-datepicker";
import { tr } from "date-fns/locale";
import InfoModal from "./modals/InfoModals";
import LinkModal from "./modals/LinkModal";
import WEditor from "./components/WEditor";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL; // Removed
const MAX_TOTAL = 30 * 1024 * 1024;
const MAX_COVER_MB = 2;
const MAX_MEDIA_MB = 16;

const safeFileSize = (fileObj) => {
  const rawSize =
    fileObj && typeof fileObj === "object" ? Number(fileObj.size) : 0;
  return Number.isFinite(rawSize) && rawSize > 0 ? rawSize : 0;
};

const filenameFromUrl = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    const name = u.pathname.split("/").pop() || "";
    return decodeURIComponent(name);
  } catch {
    const parts = (url || "").split("/");
    return decodeURIComponent(parts[parts.length - 1] || url);
  }
};

const sanitizeText = (text) => {
  if (!text) return "";
  return text
    .replace(/\r?\n|\r/g, " ")
    .replace(/\t/g, " ")
    .replace(/\s\s+/g, " ")
    .trim();
};

const buildQuizPayload = (assessment) => {
  if (!assessment?.enabled) return null;

  const normalizedQuestions = (assessment.questions || [])
    .map((q) => {
      const text = sanitizeText(q.text || q.question || "");
      if (!text) return null;

      const rawOptions = Array.isArray(q.options) ? q.options : [];
      const options = rawOptions
        .map((opt) => sanitizeText(opt))
        .filter(Boolean);

      const rawCorrectOption =
        typeof q.correctOption === "number"
          ? rawOptions[q.correctOption] || ""
          : typeof q.correctOption === "string"
            ? q.correctOption
            : "";

      return {
        id: q.id,
        text,
        options,
        correctOption: sanitizeText(rawCorrectOption),
        correctFeedback: sanitizeText(q.correctFeedback || ""),
        wrongFeedback: sanitizeText(q.wrongFeedback || ""),
        quizType: q.quizType ? q.quizType.toUpperCase() : "TEXT",
      };
    })
    .filter(Boolean);

  if (!normalizedQuestions.length) return null;

  return { questions: normalizedQuestions };
};

const CreateCourse = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [currentDateTime, setCurrentDateTime] = useState({
    date: "",
    time: "",
    datetime: null,
  });

  const getMinTimeForDatePicker = (deliveryDate) => {
    if (!deliveryDate) return new Date(0, 0, 0, 0, 0);

    const todayStr = new Date().toISOString().split("T")[0];

    // Bugün değilse → tüm saatler açık
    if (deliveryDate !== todayStr) {
      return new Date(0, 0, 0, 0, 0);
    }

    // Bugünse → şimdi + 15 dk
    const min = new Date();
    min.setMinutes(min.getMinutes() + 15);

    return min;
  };

  const parseAndValidateDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) {
      return { ok: false, message: "Tarih ve saat zorunludur." };
    }

    // HH:mm format check
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timeRegex.test(timeStr)) {
      return {
        ok: false,
        message: "Saat HH:mm formatında olmalıdır (örn: 09:30).",
      };
    }

    const dt = new Date(`${dateStr}T${timeStr}`);
    if (isNaN(dt.getTime())) {
      return { ok: false, message: "Geçersiz tarih / saat." };
    }

    return { ok: true, datetime: dt };
  };

  const validatePublishDateTime = (dateStr, timeStr) => {
    const parsed = parseAndValidateDateTime(dateStr, timeStr);
    if (!parsed.ok) return parsed;

    const selected = parsed.datetime;

    const minAllowed = new Date();
    minAllowed.setMinutes(minAllowed.getMinutes() + 15);

    if (selected < minAllowed) {
      return {
        ok: false,
        message:
          "Seçilen saat çok yakın. En az 15 dakika sonrası seçilmelidir.",
      };
    }

    return { ok: true, datetime: selected };
  };

  // ---- Gün & soru şablonları
  const questionTemplate = () => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text: "",
    options: ["", ""],
    correctOption: null,
    quizType: "TEXT",
  });

  const dayTemplate = () => ({
    lessonTitle: "",
    topicContent: "",
    completionFeedback: "",
    assessment: {
      enabled: false,
      questions: [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          text: "",
          options: ["", ""],
          correctOption: null,
          correctFeedback: "",
          wrongFeedback: "",
          quizType: "TEXT",
        },
      ],
    },
    media: { documents: [], medias: [], links: [] },
  });

  const ALLOW_MULTI_QUESTIONS = false;

  // Step-2 gün verileri + aktif gün
  const [lessons, setLessons] = useState([dayTemplate()]);
  const [activeDay, setActiveDay] = useState(0);

  // Gün sekmeleri drag–drop için
  const [dragIndex, setDragIndex] = useState(null);

  const [linkModalOpen, setLinkModalOpen] = useState(false);

  // Step 1 & 3 genel form verileri
  const [formData, setFormData] = useState({
    courseName: "",
    description: "",
    coverImage: "",
    courseLength: "1",
    deliveryDate: "",
    repostEvery: "daily",
    deliveryTime: "",
    deliveryChannel: "whatsapp",
  });

  const [coverPreview, setCoverPreview] = useState("");

  // Info modal state
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const showInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  // Gün bazında boyut hesaplama
  const calcDaySize = (day) => {
    if (!day?.media) return 0;

    const docSize = (day.media.documents || []).reduce(
      (s, f) => s + safeFileSize(f),
      0,
    );
    const mediaSize = (day.media.medias || []).reduce(
      (s, f) => s + safeFileSize(f),
      0,
    );
    return docSize + mediaSize;
  };

  useEffect(() => {
    const now = new Date();
    const date = now.toISOString().split("T")[0];
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const time = `${hours}:${minutes}`;
    setCurrentDateTime({ date, time, datetime: now });
  }, []);

  useEffect(() => {
    const len = Math.max(1, parseInt(formData.courseLength || "1", 10));
    setLessons((prev) => {
      let updated = [...prev];
      if (updated.length < len) {
        while (updated.length < len) updated.push(dayTemplate());
      } else if (updated.length > len) {
        updated = updated.slice(0, len);
      }
      return updated;
    });
    setActiveDay((d) => Math.min(d, len - 1));
  }, [formData.courseLength]);

  // ---- Step 3: tarih/saat kontrol & handler'lar
  const isDateTimeValid = (date, time) => {
    if (!date || !time) return false;
    const selected = new Date(`${date}T${time}`);
    const now = currentDateTime.datetime;
    return selected >= now;
  };

  // Step-3 gruplar
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(0); // or null

  const fetchGroups = useCallback(async () => {
    try {
      // apiClient unwrap: returns Group[] directly
      const data = await apiClient.get("/groups");
      const groupsList = Array.isArray(data) ? data : data?.groups || [];

      const formattedGroups = groupsList.map((group) => ({
        id: group.id,
        name: group.name,
        members: (group.members || []).map((member) => ({
          id: member.id,
          number: member.number,
        })),
      }));

      setGroups(formattedGroups);
    } catch (err) {
      console.error("Error fetching groups:", err);
      showInfo(
        "error",
        "Grup listesi alınamadı",
        "Gruplar yüklenirken bir hata oluştu.",
      );
    }
  }, [userId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // ---- Step 1 genel input/cover
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Kapak resmi (max 2MB) → backend'e upload → URL kaydet
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const limit = MAX_COVER_MB * 1024 * 1024;
    if (file.size > limit) {
      showInfo(
        "warning",
        "Dosya çok büyük",
        `Kapak resmi en fazla ${MAX_COVER_MB}MB olabilir.`,
      );
      e.target.value = "";
      return;
    }

    const form = new FormData();
    form.append("file", file);

    try {
      // data: { message, file: { url, ... } }
      const data = await apiClient.post("/upload/cover", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const coverUrl = data.file?.url || data.url;

      setFormData((prev) => ({ ...prev, coverImage: coverUrl }));
      setCoverPreview(URL.createObjectURL(file));
    } catch (err) {
      console.error("Cover upload error:", err);
      showInfo(
        "error",
        "Kapak yüklenemedi",
        err.formatted?.message || err.message || "Bilinmeyen hata",
      );
    }
  };

  //kapak resmi silme
  const removeCoverImage = async () => {
    try {
      // Eğer backend'e yüklenmiş bir URL varsa silmeyi dene
      if (formData.coverImage) {
        const filename = formData.coverImage.split("/").pop();
        await apiClient.delete(`/upload/${encodeURIComponent(filename)}`);
      }
    } catch (err) {
      console.warn("Kapak resmi backend'den silinemedi:", err.message);
    }

    // Frontend state temizleme
    setFormData((prev) => ({
      ...prev,
      coverImage: "",
    }));
    setCoverPreview("");
  };

  const removeMediaItem = (dayIdx, key, idx) => {
    const fileObj = lessons[dayIdx].media[key][idx];
    const url = typeof fileObj === "string" ? fileObj : fileObj.url;
    const filename = url.split("/").pop();

    setLessons((prev) =>
      prev.map((d, i) =>
        i === dayIdx
          ? {
              ...d,
              media: {
                ...d.media,
                [key]: d.media[key].filter((_, j) => j !== idx),
              },
            }
          : d,
      ),
    );

    apiClient
      .delete(`/upload/${encodeURIComponent(filename)}`)
      .then((res) => console.log("Silindi:", filename, res))
      .catch((err) => console.error("Sunucudan silme hatası:", err));
  };

  // ---- Upload helper
  const uploadFile = async (file, type) => {
    const allowedMediaTypes = [
      "video/mp4",
      "image/jpeg",
      "image/png",
      "image/webp",
    ];
    const allowedDocumentTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (type === "media" && !allowedMediaTypes.includes(file.type)) {
      showInfo(
        "warning",
        "Desteklenmeyen medya",
        "Sadece MP4, JPG, PNG veya WEBP dosyaları yükleyebilirsiniz.",
      );
      return null;
    }

    if (type === "document" && !allowedDocumentTypes.includes(file.type)) {
      showInfo(
        "warning",
        "Desteklenmeyen doküman",
        "Sadece PDF, DOC veya DOCX dosyaları yükleyebilirsiniz.",
      );
      return null;
    }

    // Medya dosyaları için 16MB limit kontrolü (WhatsApp limiti)
    if (type === "media") {
      const maxMediaSize = MAX_MEDIA_MB * 1024 * 1024;
      if (file.size > maxMediaSize) {
        showInfo(
          "warning",
          "Dosya çok büyük",
          `Medya dosyası en fazla ${MAX_MEDIA_MB}MB olabilir.`,
        );
        return null;
      }
    }

    // Aktif gün bazında boyut kontrolü
    const currentDaySize = calcDaySize(lessons[activeDay]);
    if (currentDaySize + file.size > MAX_TOTAL) {
      showInfo(
        "warning",
        "Limit aşıldı",
        "Bu içeriğin toplam dosya boyutu 30MB'ı geçemez!",
      );
      return null;
    }

    const form = new FormData();
    form.append("file", file);
    try {
      const data = await apiClient.post(`/upload/${type}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return {
        url: data.file?.url || data.url,
        // Frontend limiti için backend response size yerine gerçek dosya boyutu kullanılır.
        size: file.size,
      };
    } catch (err) {
      console.error(`${type} upload error:`, err);
      const errorMessage =
        err.formatted?.message ||
        (type === "media"
          ? `Medya dosyası yüklenemedi. Dosya boyutu ${MAX_MEDIA_MB}MB'ı geçemez.`
          : `${type} yüklenemedi.`);
      showInfo("error", "Yükleme hatası", errorMessage);
      return null;
    }
  };

  // ---- Step 2: Gün alanı helper'ları
  const updateLessonField = (idx, field, value) => {
    setLessons((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, [field]: value } : d)),
    );
  };

  const toggleAssessmentEnabled = (idx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== idx) return d;
        const enabled = !d.assessment.enabled;
        const nextQuestions =
          enabled && d.assessment.questions.length === 0
            ? [questionTemplate()]
            : d.assessment.questions.slice(
                0,
                ALLOW_MULTI_QUESTIONS ? undefined : 1,
              );
        return {
          ...d,
          assessment: {
            ...d.assessment,
            enabled,
            questions: nextQuestions,
          },
        };
      }),
    );
  };

  const deleteQuestion = (dayIdx, qIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.filter((_, j) => j !== qIdx);
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      }),
    );
  };

  // Yeni soru ekleme fonksiyonu
  const addQuestion = (dayIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const newQuestion = questionTemplate();
        return {
          ...d,
          assessment: {
            ...d.assessment,
            questions: [...d.assessment.questions, newQuestion],
          },
        };
      }),
    );
  };

  // Soru metni
  const updateQuestionField = (dayIdx, qIdx, value) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) =>
          j === qIdx ? { ...q, text: value } : q,
        );
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      }),
    );
  };

  // Seçenek işlemleri
  const updateOption = (dayIdx, qIdx, optIdx, value) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) => {
          if (j !== qIdx) return q;
          const ops = [...q.options];
          ops[optIdx] = value;
          return { ...q, options: ops };
        });
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      }),
    );
  };

  const addOption = (dayIdx, qIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) => {
          if (j !== qIdx) return q;
          if (q.options.length >= 5) return q;
          return { ...q, options: [...q.options, ""] };
        });
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      }),
    );
  };

  const removeOption = (dayIdx, qIdx, optIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) => {
          if (j !== qIdx) return q;
          const ops = q.options.filter((_, k) => k !== optIdx);
          let correct = q.correctOption;
          if (correct === optIdx) correct = null;
          else if (correct > optIdx) correct = correct - 1;
          return { ...q, options: ops, correctOption: correct };
        });
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      }),
    );
  };

  const setCorrect = (dayIdx, qIdx, optIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) =>
          j === qIdx ? { ...q, correctOption: optIdx } : q,
        );
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      }),
    );
  };

  // Her soru için ayrı quiz type ayarlama
  const setQuizTypeForQuestion = (dayIdx, qIdx, newType) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) =>
          j === qIdx ? { ...q, quizType: newType } : q,
        );
        return {
          ...d,
          assessment: {
            ...d.assessment,
            questions: qs,
          },
        };
      }),
    );
  };

  const setQuizType = (newType) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== activeDay) return d;

        const qs = d.assessment.questions.map((q) => ({
          ...q,
          quizType: newType,
        }));

        return {
          ...d,
          assessment: {
            ...d.assessment,
            questions: qs,
          },
        };
      }),
    );
  };

  // Gün silme
  const deleteDay = (idx) => {
    if (lessons.length <= 1) return;
    setLessons((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      setActiveDay((a) => {
        if (a === idx) return Math.max(0, idx - 1);
        if (a > idx) return a - 1;
        return a;
      });
      setFormData((p) => ({ ...p, courseLength: String(updated.length) }));
      return updated;
    });
  };

  // Gün drag–drop
  const onDragStart = (idx) => setDragIndex(idx);
  const onDragEnd = () => setDragIndex(null);
  const onDrop = (dropIdx) => {
    if (dragIndex === null || dragIndex === dropIdx) return;
    setLessons((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIndex, 1);
      arr.splice(dropIdx, 0, moved);
      setActiveDay((a) => {
        if (a === dragIndex) return dropIdx;
        if (dragIndex < dropIdx && a > dragIndex && a <= dropIdx) return a - 1;
        if (dragIndex > dropIdx && a >= dropIdx && a < dragIndex) return a + 1;
        return a;
      });
      return arr;
    });
    setDragIndex(null);
  };

  // Step geçişleri
  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (mode = "PUBLISHED") => {
    if (!formData.courseName.trim()) {
      showInfo("warning", "Eksik bilgi", "Lütfen içerik adını giriniz!");
      return;
    }

    /* ===========================
     SADECE PUBLISHED İÇİN
     =========================== */
    if (mode === "PUBLISHED") {
      if (!selectedGroupId) {
        showInfo("warning", "Grup seçimi gerekli", "Lütfen bir grup seçiniz!");
        return;
      }

      const dtCheck = validatePublishDateTime(
        formData.deliveryDate,
        formData.deliveryTime,
      );

      if (!dtCheck.ok) {
        showInfo("warning", "Geçersiz Zaman", dtCheck.message);
        return;
      }
    }

    const hasEmptyLesson = lessons.some(
      (lesson) => !lesson.lessonTitle?.trim() || !lesson.topicContent?.trim(),
    );

    if (hasEmptyLesson) {
      showInfo(
        "warning",
        "Eksik içerik",
        "Lütfen tüm dersler için başlık ve içerik giriniz!",
      );
      return;
    }



    setIsSubmitting(true);

    // handleSubmit fonksiyonunda (satır ~653)
    const sanitizedLessons = lessons.map((lesson, index) => ({
      title: sanitizeText(lesson.lessonTitle),
      content: sanitizeText(lesson.topicContent),
      day: index + 1,
      document: lesson.media.documents[0]?.url || null,
      media: lesson.media.medias[0]?.url || null,
      externalLink: lesson.media.links[0] || null,
      quiz: buildQuizPayload(lesson.assessment),

      completionFeedback: sanitizeText(lesson.completionFeedback || ""),
      completionReminderMessage: sanitizeText(
        lesson.completionReminderMessage || "",
      ),
      completionButtonText: sanitizeText(lesson.completionButtonText || ""),
    }));

    const requestBody = {
      courseData: {
        name: sanitizeText(formData.courseName),
        description: sanitizeText(formData.description),
        coverImage: formData.coverImage,
        adminId: Number(userId),
        status: mode,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      lessonsData: sanitizedLessons,
      learnerIds:
        groups
          .find((group) => group.id === selectedGroupId)
          ?.members.map((m) => m.id) || [],
      scheduleTime: formData.deliveryTime,
      startDate: formData.deliveryDate,
      frequency: formData.repostEvery,
      deliveryChannels: [formData.deliveryChannel],
    };

    try {
      // apiClient using /courses/
      const data = await apiClient.post("/courses/", requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      showInfo(
        "success",
        "İşlem tamam",
        "İçerik başarılı şekilde oluşturuldu!",
      );
      resetForm();
      navigate(`/${userId}/dashboard`);
    } catch (error) {
      console.error("İçerik oluşturma hatası:", error);
      const errorMessage =
        error.formatted?.message ||
        error.message ||
        "İçerik oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.";
      showInfo("error", "Hata", errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      courseName: "",
      description: "",
      coverImage: "",
      courseLength: "1",
      deliveryDate: "",
      deliveryTime: "",
      repostEvery: "daily",
      whatsappNotification: true,
      teamsNotification: true,
    });

    setCoverPreview("");
    setLessons([dayTemplate()]);
    setDragIndex(null);
    setActiveDay(0);
    setSelectedGroupId("");
  };

  // ------------------ RENDER ------------------
  const activeLesson = lessons[activeDay] || {
    media: { documents: [], medias: [] },
  };
  const activeDaySize = calcDaySize(activeLesson);
  const activeDayUsagePercent = Math.min(
    (activeDaySize / MAX_TOTAL) * 100,
    100,
  );

  return (
    <SharedLayout pageTitle="İÇERİK OLUŞTURMA">
      {/* Stepper */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-center overflow-x-auto no-scrollbar px-2">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <button
                  onClick={() => currentStep >= step && setCurrentStep(step)}
                  className={`px-4 sm:px-6 py-2 rounded-full ${
                    currentStep === step
                      ? "green-button text-white"
                      : "bg-gray-200 text-gray-600"
                  } ${
                    currentStep < step ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={currentStep < step}
                >
                  {step === 1 && "GİRİŞ"}
                  {step === 2 && "İÇERİK"}
                  {step === 3 && "YAYINLAMA"}
                </button>
                {step < 3 && (
                  <FiChevronRight
                    className="text-gray-400 hidden sm:inline-block"
                    size={20}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 sm:p-8 rounded-2xl shadow-sm">
        {/* STEP 1 */}
        {currentStep === 1 && (
          <div className="form-step space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center pb-4 sm:pb-6 mb-4 sm:mb-8 gap-3">
              <label className="text-gray-700 text-sm font-medium sm:mr-2">
                İÇERİK SÜRESİ:
              </label>
              <div className="flex items-center">
                <input
                  type="number"
                  name="courseLength"
                  min="1"
                  max="365"
                  value={formData.courseLength}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-full sm:ml-3 px-4 sm:px-6 py-1 text-md w-24 text-center outline-none focus:border-blue-500 transition-colors"
                />
                <span className="text-gray-600 ml-3 text-md font-medium">
                  İçerik
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-3 sm:mb-5">
                  İÇERİK ADI:
                </label>
                <div className="border-b border-gray-300 pb-1">
                  <input
                    type="text"
                    name="courseName"
                    value={formData.courseName}
                    onChange={handleInputChange}
                    className="w-full p-2 outline-none bg-transparent"
                    placeholder="İçerik adı yazınız"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-3 sm:mb-5">
                  İÇERİK KAPAK RESMİ:
                </label>
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="relative w-full sm:w-32 h-40 sm:h-32 bg-gray-100 border border-gray-300 rounded flex items-center justify-center overflow-hidden">
                    {coverPreview || formData.coverImage ? (
                      <>
                        <img
                          src={coverPreview || formData.coverImage}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />

                        {/* ❗ KAPAK SİLME BUTONU */}
                        <button
                          type="button"
                          onClick={removeCoverImage}
                          className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-1 rounded shadow hover:bg-red-700"
                        >
                          KALDIR
                        </button>
                      </>
                    ) : (
                      <span className="text-gray-400 text-xs">Önizleme</span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-600 mb-2">
                      Jpeg, gif veya png formatında, maksimum ~800×360px ve{" "}
                      {MAX_COVER_MB}MB olmalı.
                    </p>
                    <label className="inline-block text-blue-600 hover:text-blue-800 cursor-pointer text-sm underline">
                      RESİM YÜKLE
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/jpg,.jpg,.jpeg,.png,.webp"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-3 sm:mb-5">
                AÇIKLAMA:
              </label>

              <div className="relative">
                <WEditor
                  key="course-description"
                  value={formData.description}
                  onChange={(val) =>
                    setFormData((prev) => ({ ...prev, description: val }))
                  }
                />
              </div>
            </div>

            <div className="pt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <button
                className="w-full md:w-auto px-6 py-2 red-button text-white rounded-full flex items-center justify-center"
                onClick={() => navigate(`/${userId}/dashboard`)}
              >
                <FiX className="mr-2" /> İPTAL
              </button>
              <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="w-full sm:w-auto px-6 py-2 green-button text-white rounded-full flex items-center justify-center"
                >
                  İLERİ <FiChevronRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {currentStep === 2 && (
          <div className="form-step">
            <div className="grid grid-cols-12 gap-4 sm:gap-6">
              {/* Sol: Gün sekmeleri */}
              <div className="col-span-12 md:col-span-3">
                <div className="p-3 bg-[#07183c] rounded-2xl">
                  <div className="text-white text-sm font-medium px-1 pb-2">
                    İçerik Günleri
                  </div>
                  <ul className="max-h-80 overflow-auto pr-1">
                    {lessons.map((_, i) => {
                      const isActive = activeDay === i;
                      return (
                        <li
                          key={i}
                          className="mb-2"
                          draggable
                          onDragStart={() => onDragStart(i)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => onDrop(i)}
                          onDragEnd={onDragEnd}
                        >
                          <div
                            className={`flex items-center justify-between px-3 py-2 rounded-lg transition-colors
                              ${
                                isActive
                                  ? "bg-white/10 text-white font-extrabold"
                                  : "text-white/70 hover:text-white font-medium"
                              }`}
                          >
                            <button
                              className="flex-1 text-left"
                              onClick={() => setActiveDay(i)}
                            >
                              {i + 1}.İçerik
                            </button>
                            <div className="flex items-center gap-2 ml-3">
                              <FiMove
                                className="opacity-70 cursor-grab"
                                title="Drag to reorder"
                              />
                              <button
                                type="button"
                                onClick={() => deleteDay(i)}
                                className={`opacity-70 hover:opacity-100 ${
                                  lessons.length <= 1
                                    ? "cursor-not-allowed"
                                    : ""
                                }`}
                                disabled={lessons.length <= 1}
                                title={
                                  lessons.length <= 1
                                    ? "En az 1 gün olması gerekiyor"
                                    : "Günü sil"
                                }
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Sağ: Seçili güne ait içerik */}
              <div className="col-span-12 md:col-span-9 space-y-6">
                {/* Lesson Title */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    İÇERİK BAŞLIĞI
                  </label>
                  <div className="border-b border-gray-300 pb-1">
                    <input
                      type="text"
                      value={lessons[activeDay]?.lessonTitle || ""}
                      onChange={(e) =>
                        updateLessonField(
                          activeDay,
                          "lessonTitle",
                          e.target.value,
                        )
                      }
                      className="w-full p-2 outline-none bg-transparent"
                      placeholder="İçerik başlığını yazınız"
                      required
                    />
                  </div>
                </div>

                {/* Topic Content */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    KONU İÇERİK
                  </label>

                  <WEditor
                    key={`topic-${activeDay}`}
                    value={lessons[activeDay]?.topicContent || ""}
                    onChange={(val) =>
                      updateLessonField(activeDay, "topicContent", val)
                    }
                  />
                </div>

                {/* Completion Reminder Message */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    İÇERİK TAMAMLAMA ONAY MESAJI (1 dk sonra)
                  </label>
                  <input
                    type="text"
                    value={lessons[activeDay]?.completionReminderMessage || ""}
                    onChange={(e) =>
                      updateLessonField(
                        activeDay,
                        "completionReminderMessage",
                        e.target.value,
                      )
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                    placeholder="Örn: Bu dersi bitirdiysen aşağıdaki butona dokun 👇"
                  />
                </div>

                {/* Completion Button Text */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1 mt-4">
                    TAMAMLAMA BUTONU METNİ
                  </label>
                  <input
                    type="text"
                    value={lessons[activeDay]?.completionButtonText || ""}
                    onChange={(e) =>
                      updateLessonField(
                        activeDay,
                        "completionButtonText",
                        e.target.value,
                      )
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                    placeholder="Örn: Dersi Bitirdim"
                    maxLength={40}
                  />
                  <div className="text-xs text-gray-500 text-right">
                    {(lessons[activeDay]?.completionButtonText || "").length}/40
                  </div>
                </div>

                {/* Completion Feedback */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    İÇERİK TAMAMLANDIĞINDA GÖNDERİLECEK MESAJ
                  </label>
                  <input
                    type="text"
                    value={lessons[activeDay]?.completionFeedback || ""}
                    onChange={(e) =>
                      updateLessonField(
                        activeDay,
                        "completionFeedback",
                        e.target.value,
                      )
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                    placeholder="Örn: Bu dersi tamamladığınız için tebrikler! 🎉"
                  />
                </div>

                {/* Assessment */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-gray-700 text-sm font-medium">
                      DEĞERLENDİRME
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={
                          lessons[activeDay]?.assessment.enabled || false
                        }
                        onChange={() => toggleAssessmentEnabled(activeDay)}
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>

                  {lessons[activeDay]?.assessment.enabled && (
                    <div className="space-y-4">
                      {(lessons[activeDay]?.assessment.questions || []).map(
                        (q, qIdx) => (
                          <div
                            key={q.id}
                            className="border border-gray-200 bg-white rounded-xl shadow-sm p-4"
                          >
                            <div className="flex flex-col gap-3 mt-3 mb-5">
                              <label className="text-gray-700 text-sm font-medium">
                                Quiz Türü
                              </label>

                              <div className="grid sm:grid-cols-2 gap-3">
                                {/* TEXT QUIZ */}
                                <div
                                  className={`border rounded-xl p-4 cursor-pointer ${
                                    q.quizType === "TEXT"
                                      ? "border-green-500 bg-green-50"
                                      : "border-gray-300"
                                  }`}
                                  onClick={() =>
                                    setQuizTypeForQuestion(
                                      activeDay,
                                      qIdx,
                                      "TEXT",
                                    )
                                  }
                                >
                                  <div className="text-lg font-semibold">
                                    📝 Text Quiz
                                  </div>
                                  <p className="text-xs text-gray-600">
                                    Uzun metin, detaylı seçenekler. Kullanıcının
                                    yazarak cevap vermesi beklenir.
                                  </p>
                                </div>

                                {/* LIST QUIZ */}
                                <div
                                  className={`border rounded-xl p-4 cursor-pointer ${
                                    q.quizType === "LIST"
                                      ? "border-green-500 bg-green-50"
                                      : "border-gray-300"
                                  }`}
                                  onClick={() =>
                                    setQuizTypeForQuestion(
                                      activeDay,
                                      qIdx,
                                      "LIST",
                                    )
                                  }
                                >
                                  <div className="text-lg font-semibold">
                                    📋 List Quiz
                                  </div>
                                  <p className="text-xs text-gray-600">
                                    WhatsApp list arayüzü ile interaktif seçenek
                                    sunulur. (Cevaplar için Max 24 karakter)
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Kart başlık */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                              <div className="text-sm font-semibold text-gray-800">
                                SORU {qIdx + 1}
                              </div>
                              <button
                                type="button"
                                onClick={() => deleteQuestion(activeDay, qIdx)}
                                className="self-start sm:self-auto text-red-500 hover:text-red-600 flex items-center gap-2 text-sm"
                                title="Delete question"
                              >
                                <FiTrash2 /> KALDIR
                              </button>
                            </div>

                            {/* Soru metni */}
                            <div className="mb-3">
                              <input
                                type="text"
                                value={q.text}
                                onChange={(e) =>
                                  updateQuestionField(
                                    activeDay,
                                    qIdx,
                                    e.target.value,
                                  )
                                }
                                className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                                placeholder="Sorunuzu yazınız..."
                                maxLength={
                                  q.quizType === "LIST" ? 140 : undefined
                                }
                              />
                              {q.quizType === "LIST" && (
                                <div className="text-xs text-gray-500 text-right mt-1">
                                  {q.text.length}/140
                                </div>
                              )}
                            </div>

                            {/* Seçenekler */}
                            <div>
                              <label className="block text-gray-700 text-xs mb-2">
                                SEÇENEKLER
                              </label>
                              {q.options.map((opt, optIdx) => (
                                <div
                                  key={optIdx}
                                  className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2"
                                >
                                  <div className="flex items-center">
                                    <input
                                      type="radio"
                                      name={`correct-${activeDay}-${qIdx}`}
                                      checked={q.correctOption === optIdx}
                                      onChange={() =>
                                        setCorrect(activeDay, qIdx, optIdx)
                                      }
                                      className="mr-2"
                                    />
                                    <span className="text-xs text-gray-500 sm:hidden">
                                      Doğru
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <input
                                      type="text"
                                      value={opt}
                                      onChange={(e) =>
                                        updateOption(
                                          activeDay,
                                          qIdx,
                                          optIdx,
                                          e.target.value,
                                        )
                                      }
                                      maxLength={
                                        q.quizType === "LIST" ? 24 : undefined
                                      }
                                      className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                                      placeholder={`Seçenek ${optIdx + 1}`}
                                    />
                                    {q.quizType === "LIST" && (
                                      <div className="text-xs text-gray-500 text-right">
                                        {opt.length}/24
                                      </div>
                                    )}
                                  </div>
                                  {optIdx >= 2 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeOption(activeDay, qIdx, optIdx)
                                      }
                                      className="sm:ml-2 text-red-500 hover:text-red-700 self-start sm:self-auto"
                                    >
                                      <FiX size={18} />
                                    </button>
                                  )}
                                </div>
                              ))}

                              {q.options.length < 5 && (
                                <button
                                  onClick={() => addOption(activeDay, qIdx)}
                                  className="mt-2 px-4 py-2 border border-green-500 text-green-500 rounded-full text-sm hover:bg-green-50 transition-colors flex items-center gap-2"
                                >
                                  <FiPlus /> SEÇENEK EKLE
                                </button>
                              )}
                            </div>
                            {/* Feedback Messages */}
                            <div className="mt-4 grid sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-gray-700 text-xs mb-1">
                                  DOĞRU CEVAP MESAJI
                                </label>
                                <input
                                  type="text"
                                  value={q.correctFeedback || ""}
                                  onChange={(e) =>
                                    setLessons((prev) =>
                                      prev.map((d, i) => {
                                        if (i !== activeDay) return d;
                                        const qs = d.assessment.questions.map(
                                          (qq, j) =>
                                            j === qIdx
                                              ? {
                                                  ...qq,
                                                  correctFeedback:
                                                    e.target.value,
                                                }
                                              : qq,
                                        );
                                        return {
                                          ...d,
                                          assessment: {
                                            ...d.assessment,
                                            questions: qs,
                                          },
                                        };
                                      }),
                                    )
                                  }
                                  className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                                  placeholder="Örn: Doğru! 👏 Harika gidiyorsun!"
                                  maxLength={120}
                                />
                              </div>
                              <div>
                                <label className="block text-gray-700 text-xs mb-1">
                                  YANLIŞ CEVAP MESAJI
                                </label>
                                <input
                                  type="text"
                                  value={q.wrongFeedback || ""}
                                  onChange={(e) =>
                                    setLessons((prev) =>
                                      prev.map((d, i) => {
                                        if (i !== activeDay) return d;
                                        const qs = d.assessment.questions.map(
                                          (qq, j) =>
                                            j === qIdx
                                              ? {
                                                  ...qq,
                                                  wrongFeedback: e.target.value,
                                                }
                                              : qq,
                                        );
                                        return {
                                          ...d,
                                          assessment: {
                                            ...d.assessment,
                                            questions: qs,
                                          },
                                        };
                                      }),
                                    )
                                  }
                                  className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                                  placeholder="Örn: Yanlış ❌ ama denemeye devam et!"
                                  maxLength={120}
                                />
                              </div>
                            </div>
                          </div>
                        ),
                      )}

                      {/* Soru Ekle Butonu - Sağ Alt Köşe */}
                      <div className="flex justify-end mt-4">
                        <button
                          type="button"
                          onClick={() => addQuestion(activeDay)}
                          className="px-4 py-2 bg-green-500 text-white rounded-full text-sm hover:bg-green-600 transition-colors flex items-center gap-2 shadow-md"
                        >
                          <FiPlus /> SORU EKLE
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Media Resources */}
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label className="text-gray-700 text-sm font-medium">
                      MEDYA KAYNAKLARI
                    </label>
                    <div className="flex items-center space-x-3 text-sm">
                      {/* Document Upload */}
                      <label className="text-blue-600 hover:underline cursor-pointer">
                        DOKÜMAN YÜKLE
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file) return;
                            const uploaded = await uploadFile(file, "document");
                            if (!uploaded) return;
                            setLessons((prev) =>
                              prev.map((d, i) =>
                                i === activeDay
                                  ? {
                                      ...d,
                                      media: {
                                        ...d.media,
                                        documents: [uploaded],
                                      },
                                    }
                                  : d,
                              ),
                            );
                          }}
                        />
                      </label>

                      {/* Media Upload (video/audio/image) */}
                      <label className="text-blue-600 hover:underline cursor-pointer">
                        MEDYA YÜKLE
                        <input
                          type="file"
                          accept="video/mp4,.mp4,image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file) return;
                            const uploaded = await uploadFile(file, "media");
                            if (!uploaded) return;
                            setLessons((prev) =>
                              prev.map((d, i) =>
                                i === activeDay
                                  ? {
                                      ...d,
                                      media: {
                                        ...d.media,
                                        medias: [uploaded],
                                      },
                                    }
                                  : d,
                              ),
                            );
                          }}
                        />
                      </label>

                      {/* External Link */}
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => setLinkModalOpen(true)}
                      >
                        HARİCİ LİNK
                      </button>
                    </div>
                  </div>

                  {/* Listeler */}
                  <div className="grid md:grid-cols-3 gap-4 mt-4">
                    {/* Documents */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-sm mb-2">
                        Dokümanlar
                      </div>
                      <ul className="space-y-2">
                        {(lessons[activeDay]?.media?.documents || []).length ===
                        0 ? (
                          <li className="text-xs text-gray-500">
                            Henüz doküman yok.
                          </li>
                        ) : (
                          lessons[activeDay].media.documents.map((u, idx) => (
                            <li
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <a
                                href={u.url}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-blue-600 hover:underline"
                                title={u.url}
                              >
                                {filenameFromUrl(u.url)}
                              </a>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={() =>
                                  removeMediaItem(activeDay, "documents", idx)
                                }
                                title="Kaldır"
                              >
                                <FiTrash2 />
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>

                    {/* Medias */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-sm mb-2">Medyalar</div>
                      <ul className="space-y-2">
                        {(lessons[activeDay]?.media?.medias || []).length ===
                        0 ? (
                          <li className="text-xs text-gray-500">
                            Henüz medya yok.
                          </li>
                        ) : (
                          lessons[activeDay].media.medias.map((u, idx) => (
                            <li
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <a
                                href={u.url}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-blue-600 hover:underline"
                                title={u.url}
                              >
                                {filenameFromUrl(u.url)}
                              </a>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={() =>
                                  removeMediaItem(activeDay, "medias", idx)
                                }
                                title="Kaldır"
                              >
                                <FiTrash2 />
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>

                    {/* Links */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-sm mb-2">Linkler</div>
                      <ul className="space-y-2">
                        {(lessons[activeDay]?.media?.links || []).length ===
                        0 ? (
                          <li className="text-xs text-gray-500">
                            Henüz link yok.
                          </li>
                        ) : (
                          lessons[activeDay].media.links.map((u, idx) => (
                            <li
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <a
                                href={u}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-blue-600 hover:underline"
                                title={u}
                              >
                                {u}
                              </a>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={() =>
                                  removeMediaItem(activeDay, "links", idx)
                                }
                                title="Kaldır"
                              >
                                <FiTrash2 />
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-gray-600">
                    Yüklenen: {(activeDaySize / (1024 * 1024)).toFixed(2)} MB /
                    30 MB
                  </div>
                  <div className="w-full bg-gray-200 h-2 rounded">
                    <div
                      className="bg-green-500 h-2 rounded"
                      style={{
                        width: `${activeDayUsagePercent}%`,
                      }}
                    />
                  </div>

                  {/* Buttons */}
                  <div className="pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <button
                      className="w-full md:w-auto px-6 py-2 red-button text-white rounded-full flex items-center justify-center"
                      onClick={() => navigate(`/${userId}/dashboard`)}
                    >
                      <FiX className="mr-2" /> İPTAL
                    </button>

                    <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={prevStep}
                        className="w-full sm:w-auto px-6 py-2 border border-green-500 text-green-500 bg-transparent rounded-full flex items-center justify-center hover:bg-green-50"
                      >
                        <FiChevronLeft className="mr-2" /> GERİ
                      </button>
                      <button
                        onClick={() => {
                          handleSubmit("DRAFT");
                        }}
                        disabled={isSubmitting}
                        className={`w-full sm:w-auto px-6 py-2 ${
                          isSubmitting ? "bg-gray-400" : "bg-[#07183c]"
                        } text-white rounded-full flex items-center justify-center min-w-24`}
                      >
                        {isSubmitting ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        ) : null}
                        {isSubmitting ? "KAYDEDİLİYOR..." : "KAYDET"}
                      </button>
                      <button
                        onClick={nextStep}
                        className="w-full sm:w-auto px-6 py-2 green-button text-white rounded-full flex items-center justify-center"
                      >
                        İLERİ <FiChevronRight className="ml-2" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {currentStep === 3 && (
          <div className="form-step">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  YAYINLAMA SEÇENEKLERİ
                </h3>
                <p className="text-gray-600 text-sm">
                  Oluşturmuş olduğunuz yayının ilk içeriğini göndermek için en
                  uygun zamanı seçin (diğer tüm içerikler her tekrarlama
                  sıklığında aynı saatte gönderilecektir).
                </p>
              </div>

              <div className="flex justify-start mt-2 mb-2">
                <select
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white text-gray-700 w-full max-w-md"
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(Number(e.target.value))}
                >
                  <option value="">KATILIMCI LİSTESİ</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-gray-700 text-sm font-medium w-full sm:w-40">
                  YAYINLAMA TARİHİ:
                </label>
                <DatePicker
                  selected={
                    formData.deliveryDate
                      ? new Date(formData.deliveryDate)
                      : null
                  }
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      deliveryDate: date
                        ? date.toLocaleDateString("en-CA") // YYYY-MM-DD formatı, yerel saatle
                        : "",
                      deliveryTime:
                        date &&
                        date.toDateString() === new Date().toDateString() &&
                        prev.deliveryTime <
                          new Date().toLocaleTimeString("tr-TR", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                          ? new Date().toLocaleTimeString("tr-TR", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })
                          : prev.deliveryTime,
                    }))
                  }
                  dateFormat="dd-MM-yyyy"
                  locale={tr}
                  placeholderText="01-01-2000"
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white w-full sm:w-auto"
                  minDate={new Date()}
                  isClearable
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-gray-700 text-sm font-medium w-full sm:w-40">
                  TEKRARLAMA SIKLIĞI:
                </label>
                <select
                  name="repostEvery"
                  value={formData.repostEvery}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white w-full sm:w-32"
                >
                  <option value="daily">Gün</option>
                  <option value="weekly">Hafta</option>
                  <option value="monthly">Ay</option>
                </select>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-gray-700 text-sm font-medium w-full sm:w-40">
                  TEKRARLAMA ZAMANI:
                </label>
                <DatePicker
                  selected={
                    formData.deliveryTime
                      ? new Date(`1970-01-01T${formData.deliveryTime}`)
                      : null
                  }
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      deliveryTime: date
                        ? date.toLocaleTimeString("tr-TR", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                        : "",
                    }))
                  }
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={5}
                  timeCaption="Saat"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  placeholderText="00:00"
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white w-full sm:w-auto"
                  isClearable
                  minTime={getMinTimeForDatePicker(formData.deliveryDate)}
                  maxTime={new Date(0, 0, 0, 23, 59)}
                />
              </div>
            </div>

            <div className="mb-8">
              <ChannelSelector
                value={formData.deliveryChannel}
                onChange={(channel) =>
                  setFormData((prev) => ({
                    ...prev,
                    deliveryChannel: channel,
                  }))
                }
              />
            </div>

            <div className="pt-4 border-t border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <button
                className="w-full md:w-auto px-6 py-2 red-button text-white rounded-full flex items-center justify-center"
                onClick={() => navigate(`/${userId}/dashboard`)}
              >
                <FiX className="mr-2" /> İPTAL
              </button>
              <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                <button
                  onClick={prevStep}
                  className="w-full sm:w-auto px-6 py-2 border border-green-500 text-green-500 bg-transparent rounded-full flex items-center justify-center"
                >
                  <FiChevronLeft className="mr-2" /> GERİ
                </button>
                <button
                  onClick={() => {
                    handleSubmit("DRAFT");
                  }}
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-2 ${
                    isSubmitting ? "bg-gray-400" : "bg-[#07183c]"
                  } text-white rounded-full flex items-center justify-center min-w-24`}
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : null}
                  {isSubmitting ? "KAYDEDİLİYOR..." : "KAYDET"}
                </button>

                <button
                  onClick={() => handleSubmit("PUBLISHED")}
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-2 ${
                    isSubmitting ? "bg-gray-400" : "green-button"
                  } text-white rounded-full flex items-center justify-center min-w-24`}
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : null}
                  {isSubmitting ? (
                    "YAYINLANIYOR..."
                  ) : (
                    <>
                      YAYINLA <FiChevronRight className="ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <LinkModal
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        onSave={(url) => {
          setLessons((prev) =>
            prev.map((d, i) =>
              i === activeDay
                ? {
                    ...d,
                    media: {
                      ...d.media,
                      links: [...(d.media.links || []), url],
                    },
                  }
                : d,
            ),
          );
        }}
      />

      {/* INFO MODAL */}
      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={() => {
          closeInfo();
          if (info.type === "success") {
            navigate(`/${userId}/dashboard`);
          }
        }}
      />
    </SharedLayout>
  );
};

export default CreateCourse;
